<template>
  <SharedHeader :basisModule="true">
    <template #firstColumn>
      <h1>
        <SimpleText text-key="basismodule.overview.title" />
      </h1>
      <span>
        <RichText text-key="basismodule.overview.header-text" />
      </span>
    </template>
    <template #secondColumn>
      <button v-if="simpleTextOrNull('home.header.button2.link')"
              :data-cy="simpleTextOrNull('home.header.button2.link')"
              type="button"
              class="button button-secondary"
              @click="openLink(simpleTextOrNull('home.header.button2.link'))">
        <SimpleText text-key='home.header.button2.text'></SimpleText>
      </button>
      <div class="flex flex-lg-row flex-column result-buttons">

        <!-- sonar-ignore -->
        <div class="result-buttons__button-group flex">
          <button
              :id="`result-print-link`"
              class="button button-secondary"
              onclick="window.print()"
          >
            <svg aria-hidden="true" class="icon-svg" focusable="false">
              <use xlink:href="#file-download"></use>
            </svg>
            <SimpleText text-key="results.printButtonText"/>
          </button>
        </div>
      </div>
    </template>
  </SharedHeader>

  <div class="container">
    <div class="row py-6">
      <div class="col-12 px-0">
        <ElectricityCalculator
          :subgroupingId="constants.GROUP_FORBRUG_AF_ELEKTRICITET"
          :title="getContentfulString('subcategories.subgroupelectricity')"
          :tooltip="getContentfulString('basis-module.electricity.tooltip')"
          :expanded="true"
          :basisModule="true"
          @save-calculation-line="saveCalculationLine"
          @delete-calculation-line="deleteCalculationLine"
        />
        <HeatCalculator
          :subgroupingId="constants.GROUP_FJERNVARME"
          :title="getContentfulString('subcategories.subgroup-heat')"
          :tooltip="getContentfulString('basis-module.heat.tooltip')"
          :expanded="false"
          :basisModule="true"
          @save-calculation-line="saveCalculationLine"
          @delete-calculation-line="deleteCalculationLine"
        />
        <FuelCalculator
          :subgroupingId="constants.GROUP_BRÆNDSLER"
          :title="getContentfulString('subcategories.subgroup-fuel')"
          :tooltip="getContentfulString('basis-module.fuel.tooltip')"
          :expanded="false"
          :basisModule="true"
          @save-calculation-line="saveCalculationLine"
          @delete-calculation-line="deleteCalculationLine"
        />
        <ProcessCalculator
          :subgroupingId="constants.GROUP_PROCESUDLEDNING"
          :title="getContentfulString('subcategories.subgroup-process')"
          :tooltip="getContentfulString('basis-module.process.tooltip')"
          :expanded="false"
          :basisModule="true"
          @save-calculation-line="saveCalculationLine"
          @delete-calculation-line="deleteCalculationLine"
        />
        <CommonTransportCalculator
          :subgroupingId="constants.GROUP_EGNE_OG_LEASEDE_KØRETØJER"
          :title="getContentfulString('group.text.category.groupownleasedtransports')"
          :tooltip="getContentfulString('basis-module.leasedtransports.tooltip')"
          :expanded="false"
          :basisModule="true"
          @save-calculation-line="saveCalculationLine"
          @delete-calculation-line="deleteCalculationLine"
        />

        <ResultsESG v-if="results.esgResult && this.anonymousCalculationLines.length > 0"
                    :results="results"
                    :basisModule="true"
        />

      </div>
    </div>

    <div class="row py-6">
      <div class="col-12 px-0">
        <h1 id="description-section-main-header">
          <SimpleText v-if="!basisModule" text-key="basis-module.description.header" />
        </h1>
        <AccordionComponent :accordion-list='getAccordions("basis-module")'
                            :unique-id='getUniqueId()'></AccordionComponent>
      </div>
    </div>
  </div>
</template>

<script lang='ts'>
import { Options } from 'vue-class-component'
import BaseComponent from '@/components/calculator/base-components/BaseComponent'
import SimpleText from '@/components/SimpleText.vue'
import RichText from '@/components/RichText.vue'
import SharedHeader from '@/components/calculator/shared/SharedHeader.vue'
import ElectricityCalculator from '../energy-and-processes/calculators/ElectricityCalculator.vue'
import {
  Calculate, Calculation, CalculationPurposePurposeEnum, CalculationStateEnum,
  GroupingCalculatedResults
} from 'openapi/generated-clients/climatecompass/api'
import { calculationClient } from '@/shared/BackendService'
import FuelCalculator from '@/components/calculator/energy-and-processes/calculators/FuelCalculator.vue'
import ResultsESG from '@/components/calculator/results/ResultsESG.vue'
import HeatCalculator from '@/components/calculator/energy-and-processes/calculators/HeatCalculator.vue'
import ProcessCalculator from '@/components/calculator/energy-and-processes/calculators/ProcessCalculator.vue'
import CommonTransportCalculator from '@/components/calculator/transport/calculators/CommonTransportCalculator.vue'
import { Cloneable } from '@/shared/DeepCopyHelper'
import AccordionComponent from "@/components/AccordionComponent.vue";
import {v4 as uuid} from "uuid";

@Options({
  components: {
    AccordionComponent,
    CommonTransportCalculator,
    ProcessCalculator,
    HeatCalculator,
    ResultsESG,
    FuelCalculator,
    RichText,
    SharedHeader,
    SimpleText,
    ElectricityCalculator
  }
})
export default class BasisModuleView extends BaseComponent {
  results: GroupingCalculatedResults = {}
  anonymousCalculation: Calculation = {
    id: undefined,
    privateCalculation: false,
    vatNo: '',
    calculationPurpose: {
      name: 'AnonymousCalculation',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().getFullYear() + '-12-31',
      purpose: 'studyOrOther' as CalculationPurposePurposeEnum
    },
    contactPersonRID: '111111',
    state: 'draft' as CalculationStateEnum.Draft,
    rfi: false,
    electricityEnvironmentDeclaration: true
  }
  anonymousCalculationLines: Array<Calculate> = []

  async saveCalculationLine(line: Calculate) {
    let hit = false
    this.anonymousCalculationLines.forEach((l: Calculate) => {
      if (l.id === line.id) {
        Object.assign(l, line)
        hit = true
      }
    })
    // add/edit
    if (!hit) {
      this.anonymousCalculationLines.push(line)
    }
    await this.populateResults(this.anonymousCalculationLines, line.id)
  }

  async deleteCalculationLine(lineId: number) {
    // Remove the matching line
    this.anonymousCalculationLines = this.anonymousCalculationLines.filter(l => l.id !== lineId)
    await this.populateResults(this.anonymousCalculationLines, lineId)
  }

  async populateResults(lines: Array<Calculate>, lineId: number | undefined) {
    const calculationAndLines = {
      calculation: this.anonymousCalculation,
      lines: lines
    }
    await calculationClient
      .getBaseModuleResultCalculations(calculationAndLines)
      .then(async (result) => {
        this.results = result.data
      })
      .catch(error => {
        console.error(error)
      })
    // deepcopy to avoid mutex error
    this.store.commit('setAnonymousCalculationLines', Cloneable.deepCopy(lines))
  }

  /* printPDF() {
     const url = `/klimakompasset/print-results/todo}`;
     window.open(url, "_blank");
   }

   get printTooltip() {
     return this.store.getters.getContentfulContent.findSimpleText('results.printTooltip')
   }*/

  openLink(url: string) {
    window.open(url)
  }

  simpleTextOrNull(textKey: string) {
    return this.store.getters.getContentfulContent.findSimpleTextOrNull(textKey)
  }

  getUniqueId() {
    return uuid()
  }

}
</script>